"use client";

import type {
  BlockContent as BlockContentType,
  Faqs as FAQsProps,
} from "@types";
import Image from "next/image";
import { useState } from "react";
import { DotsIcon } from "@/components/icons/dots";
import { spacing, typography } from "@/styles/design-tokens";
import AnimatedTitle from "./inputs/AnimatedTitle";
import { BlockContent } from "./inputs/PortableTextComponents";

type AccordionProps = {
  question?: string;
  answer?: BlockContentType;
};

function AccordionItem({ question, answer }: AccordionProps) {
  const [isOpen, setIsOpen] = useState(false);

  if (!question || !answer) return null;

  return (
    <button
      type="button"
      className={`${isOpen ? "shadow-none translate-2 bg-yellow/90" : ""} w-full flex flex-col items-start text-left gap-4 p-4 rounded-2xl border-2 border-black bg-[#FFF1CC] shadow-[8px_8px_0_0_rgba(0,0,0,0.25)] transition-all cursor-pointer hover:shadow-none hover:translate-2 lg:p-6`}
      onClick={() => setIsOpen(!isOpen)}
      aria-expanded={isOpen}
      tabIndex={0}
    >
      <div className="grid grid-cols-[1fr_24px] gap-4 w-full">
        <h5 className={typography.captionLarge}>{question}</h5>
        <span
          className={`transition-transform duration-300 mb-1 ${isOpen ? "rotate-45" : ""}`}
        >
          <Image
            src="/images/plus.svg"
            alt="plus icon"
            width={24}
            height={24}
          />
        </span>
      </div>
      {isOpen && <BlockContent value={answer} classes="w-full" />}
    </button>
  );
}

export default function FAQs({ title, copy, accordion }: FAQsProps) {
  if (!accordion) return null;

  return (
    <section className={`${spacing.outer} bg-purple`}>
      <div className={`${spacing.inner} relative overflow-hidden`}>
        <div className="text-center space-y-4 max-w-5xl mx-auto relative z-10">
          {title && (
            <AnimatedTitle
              className={`${typography.h4} text-yellow text-stroke`}
              text={title}
            />
          )}
          {copy && <BlockContent value={copy} />}
        </div>

        {Array.isArray(accordion) && accordion.length > 0 && (
          <div className="space-y-6 max-w-[800px] mx-auto relative z-10">
            {accordion.map((item) => (
              <AccordionItem key={item._key} {...item} />
            ))}
          </div>
        )}
        <Image
          src="/images/trunks-character.webp"
          alt="illustration of trunks character"
          width={821}
          height={1026}
          className="absolute -bottom-1/5 -left-1/2 lg:-left-1/3"
        />
        <Image
          src="/images/jordan-character.webp"
          alt="illustration of trunks character"
          width={801}
          height={1001}
          className="absolute -bottom-1/12 -right-6/12 lg:-right-4/12"
        />
      </div>
      <DotsIcon classes="absolute -top-6 -left-6" color="#6dcfd0" />
      <DotsIcon
        classes="absolute rotate-180 -bottom-6 -right-6"
        color="#6dcfd0"
      />
    </section>
  );
}
